import { extractPaths } from '@modules/aside/helpers'
import { AsideMenu, AsideTab, AsideTabTypeEnum } from '@modules/aside/model'

import { PagePermission } from '@/constants/pagePermission'

export const AsideDefaultMenu: ReadonlyArray<AsideMenu> = [
  {
    to: '/dashboard',
    fontIcon: 'fa-solid fa-gauge',
    intlMessage: 'menu.dashboard',
  },
  {
    to: '',
    intlMessage: 'menu.appointment',
    fontIcon: 'fa-solid fa-calendar-days',
    children: [
      {
        hasBullet: true,
        to: '/appointment-calendar',
        intlMessage: 'menu.appointment_calendar',
        permissions: PagePermission.APPOINTMENT_CALENDAR,
      },
      {
        hasBullet: true,
        to: '/appointment-time-offs',
        intlMessage: 'menu.appointment_time_off',
        permissions: PagePermission.APPOINTMENT_DOCTOR_TIME_OFF_LIST,
      },
      {
        hasBullet: true,
        to: '/appointment-time-slots',
        intlMessage: 'menu.appointment_time_slot',
        permissions: PagePermission.APPOINTMENT_TIME_SLOT_DOCTOR_LIST,
      },
      {
        hasBullet: true,
        to: '/appointment-settings',
        intlMessage: 'menu.appointment_setting',
        permissions: PagePermission.APPOINTMENT_SETTING,
      },
    ],
  },
  {
    to: '',
    intlMessage: 'menu.day_off_configuration',
    fontIcon: 'fa-solid fa-sliders',
    children: [
      {
        hasBullet: true,
        to: '/admin/booking-holidays',
        intlMessage: 'menu.booking_holiday',
        permissions: PagePermission.ADMIN_BOOKING_HOLIDAY_LIST,
      },
      {
        hasBullet: true,
        to: '/clinic-booking-closed-times',
        intlMessage: 'menu.clinic_booking_closed_time',
        permissions: PagePermission.CLINIC_BOOKING_CLOSED_TIME_LIST,
      },
    ],
  },
  {
    to: '/tasks',
    fontIcon: 'fa-solid fa-list-check',
    intlMessage: 'menu.task_management',
    permissions: PagePermission.TASK_LIST,
  },
]

export const AsideMedicalMenu: ReadonlyArray<AsideMenu> = [
  {
    to: '/patient-visits',
    fontIcon: 'fa-solid fa-address-book',
    intlMessage: 'menu.patient_visit',
    permissions: PagePermission.PATIENT_VISIT_LIST,
  },
  {
    to: '/patient-vital-checks',
    fontIcon: 'fa-solid fa-heart-pulse',
    intlMessage: 'menu.patient_vital_check',
    permissions: PagePermission.PATIENT_VITAL_CHECK_LIST,
  },
  {
    to: '/medical-record',
    fontIcon: 'fa-solid fa-book-medical',
    intlMessage: 'menu.medical_record',
    permissions: PagePermission.MEDICAL_RECORD,
  },
  {
    to: '/admin/medical-record-templates',
    fontIcon: 'fa-solid fa-paste',
    intlMessage: 'menu.medical_record_template',
    permissions: PagePermission.ADMIN_MEDICAL_RECORD_TEMPLATE_LIST,
  },
  {
    to: '/treatments',
    fontIcon: 'fa-solid fa-hands-holding-child',
    intlMessage: 'menu.treatment',
    permissions: PagePermission.TREATMENT_LIST,
  },
  {
    to: '',
    fontIcon: 'fa-solid fa-hands-holding-child',
    intlMessage: 'menu.treatment',
    children: [
      {
        to: '/admin/treatments',
        intlMessage: 'menu.treatment.list',
        hasBullet: true,
        permissions: PagePermission.ADMIN_TREATMENT_LIST,
      },
      {
        to: '/admin/treatment-categories',
        intlMessage: 'menu.treatment.categories',
        hasBullet: true,
        permissions: PagePermission.ADMIN_TREATMENT_CATEGORIES_LIST,
      },
    ],
  },
  {
    to: '/examinations',
    fontIcon: 'fa-solid fa-magnifying-glass',
    intlMessage: 'menu.examination',
    permissions: PagePermission.EXAMINATION_LIST,
  },
  {
    to: '',
    fontIcon: 'fa-solid fa-magnifying-glass',
    intlMessage: 'menu.examination',
    children: [
      {
        to: '/admin/examinations',
        intlMessage: 'menu.examination.list',
        hasBullet: true,
        permissions: PagePermission.ADMIN_EXAMINATION_LIST,
      },
      {
        to: '/admin/examination-categories',
        intlMessage: 'menu.examination.categories',
        hasBullet: true,
        permissions: PagePermission.ADMIN_EXAMINATION_CATEGORY_LIST,
      },
    ],
  },
  {
    to: '/admin/icd-10-trendings',
    fontIcon: 'fa-solid fa-code',
    intlMessage: 'menu.icd10s',
    permissions: PagePermission.ADMIN_ICD_10_TRENDING,
  },
  {
    to: '/icd-10-trendings',
    fontIcon: 'fa-solid fa-code',
    intlMessage: 'menu.icd10s',
    permissions: PagePermission.ICD_10_TRENDING,
  },
  {
    to: '',
    fontIcon: 'fa-solid fa-file-medical',
    intlMessage: 'menu.diagnostic_ancillary',
    children: [
      {
        to: '/diagnostic-ancillaries',
        intlMessage: 'menu.diagnostic_ancillary_list',
        hasBullet: true,
        permissions: PagePermission.DIAGNOSTIC_ANCILLARY_LIST,
      },
      {
        to: '/diagnostic-ancillary-actions',
        intlMessage: 'menu.diagnostic_ancillary_action',
        hasBullet: true,
        permissions: PagePermission.DIAGNOSTIC_ANCILLARY_LIST_ACTION,
      },
      {
        to: '/diagnostic-ancillary-summaries',
        intlMessage: 'menu.diagnostic_ancillary_summary',
        hasBullet: true,
        permissions: PagePermission.DIAGNOSTIC_ANCILLARY_LIST_SUMMARY,
      },
      {
        to: '/diagnostic-ancillary-templates',
        intlMessage: 'menu.diagnostic_ancillary_template',
        hasBullet: true,
        permissions: PagePermission.DIAGNOSTIC_ANCILLARY_TEMPLATE_LIST,
      },
    ],
  },
]

export const AsidePOSMenu: ReadonlyArray<AsideMenu> = [
  {
    to: '/cashier/cart',
    fontIcon: 'fa-solid fa-money-bills',
    intlMessage: 'menu.sales',
    permissions: PagePermission.CART,
  },
  {
    to: '/clinic-manager/shifts',
    fontIcon: 'fa-solid fa-clock',
    intlMessage: 'menu.shift',
    permissions: PagePermission.CLINIC_MANAGER_SHIFT_LIST,
  },
  {
    to: '/cashier/shift',
    fontIcon: 'fa-solid fa-clock',
    intlMessage: 'menu.shift',
    permissions: PagePermission.CASHIER_SESSION_CREATE,
  },
  {
    to: '/daily-report',
    fontIcon: 'fa-solid fa-clipboard-list',
    intlMessage: 'menu.daily_report',
    permissions: PagePermission.CASHIER_DAILY_REPORT,
  },
  {
    to: '/cluster-manager/summary-report',
    fontIcon: 'fa-solid fa-paste',
    intlMessage: 'menu.summary_report',
    permissions: PagePermission.CLUSTER_MANAGER_SUMMARY_REPORT_LIST,
  },
  {
    to: '/summary-report',
    fontIcon: 'fa-solid fa-paste',
    intlMessage: 'menu.summary_report',
    permissions: PagePermission.SUMMARY_REPORT_LIST,
  },
  {
    to: '',
    fontIcon: 'fa-solid fa-boxes-stacked',
    intlMessage: 'menu.retail_product',
    children: [
      {
        to: '/admin/retail-products',
        intlMessage: 'menu.retail_product.list',
        hasBullet: true,
        permissions: PagePermission.ADMIN_RETAIL_PRODUCT_LIST,
      },
      {
        to: '/admin/retail-product-categories',
        intlMessage: 'menu.retail_product.category',
        hasBullet: true,
        permissions: PagePermission.ADMIN_RETAIL_PRODUCT_CATEGORY_LIST,
      },
    ],
  },
  {
    to: '/admin/product-bundlings',
    fontIcon: 'fa-solid fa-gift',
    intlMessage: 'menu.product_bundling',
    permissions: PagePermission.ADMIN_PRODUCT_BUNDLING_LIST,
  },
  {
    to: '/product-bundlings',
    fontIcon: 'fa-solid fa-gift',
    intlMessage: 'menu.product_bundling',
    permissions: PagePermission.PRODUCT_BUNDLING_LIST,
  },
  {
    to: '/admin/product-promotions',
    fontIcon: 'fa-solid fa-tags',
    intlMessage: 'menu.product_promotion',
    permissions: PagePermission.ADMIN_PRODUCT_PROMOTION_LIST,
  },
  {
    to: '/product-promotions',
    fontIcon: 'fa-solid fa-tags',
    intlMessage: 'menu.product_promotion',
    permissions: PagePermission.PRODUCT_PROMOTION_LIST,
  },
  {
    to: '/cashier/retail',
    fontIcon: 'fa-solid fa-cash-register',
    intlMessage: 'menu.retail',
    permissions: PagePermission.RETAIL_INDEX,
  },
  {
    to: '/cash-deposit',
    fontIcon: 'fa-solid fa-circle-dollar-to-slot',
    intlMessage: 'menu.cash_deposit',
    permissions: PagePermission.CASH_DEPOSIT_LIST,
  },
  {
    to: '/admin/cash-deposit',
    fontIcon: 'fa-solid fa-circle-dollar-to-slot',
    intlMessage: 'menu.cash_deposit',
    permissions: PagePermission.ADMIN_CASH_DEPOSIT_LIST,
  },
  {
    to: '/price-tags',
    fontIcon: 'fa-solid fa-tags',
    intlMessage: 'menu.price_tags',
    permissions: PagePermission.PRICE_TAGS,
  },
  {
    to: '',
    fontIcon: 'fa-solid fa-ticket',
    intlMessage: 'menu.voucher_management',
    children: [
      {
        to: '/admin/voucher-codes',
        hasBullet: true,
        intlMessage: 'menu.voucher_codes',
        permissions: PagePermission.ADMIN_VOUCHER_CODE_LIST,
      },
      {
        to: '/admin/voucher-generate-rules',
        hasBullet: true,
        intlMessage: 'menu.voucher_generate_rules',
        permissions: PagePermission.ADMIN_VOUCHER_GENERATE_RULE_LIST,
      },
    ],
  },
  {
    to: '/clinic-manager/sales-invoices',
    fontIcon: 'fa-solid fa-receipt',
    intlMessage: 'menu.sales_invoices',
    permissions: PagePermission.CLINIC_MANAGER_SALES_INVOICE_LIST,
  },
]

export const AsidePharmacyMenu: ReadonlyArray<AsideMenu> = [
  {
    to: '/admin/virtual-therapeutic-moieties',
    fontIcon: 'fa-solid fa-prescription',
    intlMessage: 'menu.virtual_therapeutic_moiety',
    permissions: PagePermission.ADMIN_VIRTUAL_THERAPEUTIC_MOIETY_LIST,
  },
  {
    to: '/admin/virtual-medicinal-products',
    fontIcon: 'fa-solid fa-prescription-bottle',
    intlMessage: 'menu.virtual_medicinal_product',
    permissions: PagePermission.ADMIN_VIRTUAL_MEDICINAL_PRODUCT_LIST,
  },
  {
    to: '/admin/medicines',
    intlMessage: 'menu.medicine',
    fontIcon: 'fa-solid fa-capsules',
    permissions: PagePermission.ADMIN_MEDICINE_LIST,
  },
  {
    to: '/admin/injections',
    intlMessage: 'menu.injection',
    fontIcon: 'fa-solid fa-syringe',
    permissions: PagePermission.ADMIN_INJECTION_LIST,
  },
  {
    to: '/admin/vaccines',
    intlMessage: 'menu.vaccine',
    fontIcon: 'fa-solid fa-vial-virus',
    permissions: PagePermission.ADMIN_VACCINE_LIST,
  },
  {
    to: '/admin/medical-tools',
    fontIcon: 'fa-solid fa-stethoscope',
    intlMessage: 'menu.medical_tool',
    permissions: PagePermission.MEDICAL_TOOL_LIST,
  },
  {
    to: '',
    fontIcon: 'fa-solid fa-prescription-bottle-medical',
    intlMessage: 'menu.medicinal_management',
    children: [
      {
        to: '/admin/medicinal-controlled-drug-classifications',
        intlMessage: 'menu.medicinal_controlled_drug_classification',
        hasBullet: true,
        permissions: PagePermission.ADMIN_MEDICINAL_CONTROLLED_DRUG_CLASSIFICATION_LIST,
      },
      {
        to: '/admin/medicinal-attentions',
        intlMessage: 'menu.medicinal_attention',
        hasBullet: true,
        permissions: PagePermission.ADMIN_MEDICINAL_ATTENTION_LIST,
      },
      {
        to: '/admin/medicinal-instructions',
        intlMessage: 'menu.medicinal_instruction',
        hasBullet: true,
        permissions: PagePermission.ADMIN_MEDICINAL_INSTRUCTION_LIST,
      },
      {
        to: '/admin/medicinal-forms',
        intlMessage: 'menu.medicinal_form',
        hasBullet: true,
        permissions: PagePermission.ADMIN_MEDICINAL_FORM_LIST,
      },
      {
        to: '/admin/medicinal-routes',
        intlMessage: 'menu.medicinal_route',
        hasBullet: true,
        permissions: PagePermission.ADMIN_MEDICINAL_ROUTE_LIST,
      },
      {
        to: '/admin/medicinal-classifications',
        intlMessage: 'menu.medicinal_classification',
        hasBullet: true,
        permissions: PagePermission.ADMIN_MEDICINAL_CLASSIFICATION_LIST,
      },
      {
        to: '/admin/medicinal-categories',
        intlMessage: 'menu.medicinal_category',
        hasBullet: true,
        permissions: PagePermission.ADMIN_MEDICINAL_CATEGORY_LIST,
      },
    ],
  },
  {
    to: '/admin/units',
    fontIcon: 'fa-solid fa-scale-balanced',
    intlMessage: 'menu.unit',
    permissions: PagePermission.UNIT_LIST,
  },
  {
    to: '/medicinal-dispenses',
    fontIcon: 'fa-solid fa-capsules',
    intlMessage: 'menu.medicinal_dispense',
    permissions: PagePermission.MEDICINAL_DISPENSE_LIST,
  },
  {
    to: '/product-receives',
    fontIcon: 'fa-solid fa-boxes-stacked',
    intlMessage: 'menu.product_receive_management',
    permissions: PagePermission.PRODUCT_RECEIVE_LIST,
  },
  {
    to: '/product-adjustment',
    fontIcon: 'fa-solid fa-sliders',
    intlMessage: 'menu.product_adjustment',
    permissions: PagePermission.PRODUCT_ADJUSTMENT_LIST,
  },
  {
    to: '/product-transfers',
    fontIcon: 'fa-solid fa-right-left',
    intlMessage: 'menu.product_transfer',
    permissions: PagePermission.PRODUCT_TRANSFER_LIST,
  },
  {
    to: '/product-inventories',
    fontIcon: 'fa-solid fa-boxes-stacked',
    intlMessage: 'menu.product_inventory',
    permissions: PagePermission.PRODUCT_INVENTORY_LIST,
  },
  {
    to: '',
    fontIcon: 'fa-solid fa-arrows-rotate',
    intlMessage: 'menu.clinic_stock_transfer',
    children: [
      {
        to: '/product-transfer-clinic-outbounds',
        hasBullet: true,
        intlMessage: 'menu.clinic_stock_transfer_outbound',
        permissions: PagePermission.PRODUCT_TRANSFER_CLINIC_OUTBOUND_LIST,
      },
      {
        to: '/product-transfer-clinic-inbounds',
        hasBullet: true,
        intlMessage: 'menu.clinic_stock_transfer_inbound',
        permissions: PagePermission.PRODUCT_TRANSFER_CLINIC_INBOUND_LIST,
      },
    ],
  },
]

export const AsideHRMenu: ReadonlyArray<AsideMenu> = [
  {
    to: '/admin/users',
    fontIcon: 'fa-solid fa-user',
    intlMessage: 'menu.user_management',
    permissions: PagePermission.ADMIN_USER_LIST,
  },
  {
    to: '/users',
    fontIcon: 'fa-solid fa-user',
    intlMessage: 'menu.user_management',
    permissions: PagePermission.USER_LIST,
  },
  {
    to: '/doctors',
    fontIcon: 'fa-solid fa-user-doctor',
    intlMessage: 'menu.doctor',
    permissions: PagePermission.DOCTOR_LIST,
  },
  {
    to: '/admin/doctors',
    fontIcon: 'fa-solid fa-user-doctor',
    intlMessage: 'menu.doctor',
    permissions: PagePermission.ADMIN_DOCTOR_LIST,
  },
  {
    to: '/nurses',
    fontIcon: 'fa-solid fa-user-nurse',
    intlMessage: 'menu.nurse_management',
    permissions: PagePermission.NURSE_LIST,
  },
  {
    to: '/admin/nurses',
    fontIcon: 'fa-solid fa-user-nurse',
    intlMessage: 'menu.nurse_management',
    permissions: PagePermission.ADMIN_NURSE_LIST,
  },
  {
    to: '/pharmacists',
    fontIcon: 'fa-solid fa-user-tie',
    intlMessage: 'menu.pharmacist_management',
    permissions: PagePermission.PHARMACIST_LIST,
  },
  {
    to: '/admin/pharmacists',
    fontIcon: 'fa-solid fa-user-tie',
    intlMessage: 'menu.pharmacist_management',
    permissions: PagePermission.ADMIN_PHARMACIST_LIST,
  },
  {
    to: '/admin/qualification-types',
    fontIcon: 'fa-solid fa-table-list',
    intlMessage: 'qualification_type.menu.title',
    permissions: PagePermission.ADMIN_QUALIFICATION_TYPE_LIST,
  },
  {
    to: '/admin/radiographers',
    fontIcon: 'fa-solid fa-hospital-user',
    intlMessage: 'menu.radiographer',
    permissions: PagePermission.ADMIN_RADIOGRAPHER_LIST,
  },
]

export const AsideManagementMenu: ReadonlyArray<AsideMenu> = [
  {
    to: '/companies',
    fontIcon: 'fa-solid fa-building',
    intlMessage: 'menu.company_management',
    permissions: PagePermission.COMPANY_LIST,
  },
  {
    to: '/admin/clinics',
    fontIcon: 'fa-solid fa-house-chimney-medical',
    intlMessage: 'menu.clinic_management',
    permissions: PagePermission.CLINIC_LIST,
  },
  {
    to: '/admin/suppliers',
    fontIcon: 'fa-solid fa-truck-moving',
    intlMessage: 'menu.supplier_management',
    permissions: PagePermission.ADMIN_SUPPLIER_LIST,
  },
  {
    to: '/admin/banks',
    fontIcon: 'fa-solid fa-building-columns',
    intlMessage: 'menu.banks',
    permissions: PagePermission.ADMIN_BANK_LIST,
  },
  {
    to: '/admin/client-corporates',
    fontIcon: 'fa-solid fa-building',
    intlMessage: 'menu.client_corporate',
    permissions: PagePermission.ADMIN_CLIENT_CORPORATE_LIST,
  },
  {
    to: '',
    fontIcon: 'fa-solid fa-user-shield',
    intlMessage: 'menu.insurance',
    children: [
      {
        hasBullet: true,
        to: '/admin/insurances',
        intlMessage: 'menu.insurance',
        permissions: PagePermission.ADMIN_INSURANCE_LIST,
      },
      {
        hasBullet: true,
        to: '/admin/insurance-payors',
        intlMessage: 'menu.insurance_payor',
        permissions: PagePermission.ADMIN_INSURANCE_PAYOR_LIST,
      },
    ],
  },
  {
    to: '/appointment-types',
    fontIcon: 'fa-solid fa-calendar-check',
    intlMessage: 'menu.appointment_type',
    permissions: PagePermission.APPOINTMENT_TYPE_LIST,
  },
  {
    to: '/cashiers',
    fontIcon: 'fa-solid fa-cash-register',
    intlMessage: 'menu.cashiers',
    permissions: PagePermission.CASHIER_LIST,
  },
  {
    to: '/queues',
    fontIcon: 'fa-solid fa-layer-group',
    intlMessage: 'queues',
    permissions: PagePermission.QUEUE_LIST,
  },
  {
    to: '/queue-displays',
    fontIcon: 'fa-solid fa-tv',
    intlMessage: 'queue_displays',
    permissions: PagePermission.QUEUE_DISPLAY_LIST,
  },
  {
    to: '/printer-gateways',
    fontIcon: 'fa-solid fa-print',
    intlMessage: 'menu.printer_gateway',
    permissions: PagePermission.PRINTER_GATEWAY_LIST,
  },
  {
    to: '/clinic-ottopay-edcs',
    fontIcon: 'fa-solid fa-cash-register',
    intlMessage: 'menu.clinic_ottopay_edcs',
    permissions: PagePermission.CLINIC_OTTOPAY_EDC_LIST,
  },
  {
    to: '/clinics/settings',
    intlMessage: 'menu.settings',
    fontIcon: 'fa-solid fa-gear',
    permissions: PagePermission.CLINIC_SETTING_LIST,
  },
  {
    to: '/admin/settings',
    intlMessage: 'menu.settings',
    fontIcon: 'fa-solid fa-gear',
    permissions: PagePermission.ADMIN_GLOBAL_SETTING_LIST,
  },
  {
    to: '/admin/developer-tools',
    intlMessage: 'menu.developer_tools',
    fontIcon: 'fa-solid fa-laptop-code',
    permissions: PagePermission.ADMIN_DEVELOPER_TOOLS,
  },
]

export const AsideReportMenu: ReadonlyArray<AsideMenu> = [
  {
    to: '/waive-fee-reports',
    intlMessage: 'menu.waive_fee_report',
    fontIcon: 'fa-solid fa-clipboard-list',
    permissions: PagePermission.WAIVE_FEE_REPORT,
  },
  {
    to: '',
    fontIcon: 'fa-solid fa-clipboard-list',
    intlMessage: 'menu.pharma_inventory_report',
    children: [
      {
        to: '/pharma-inventory-report/finance-inventory',
        intlMessage: 'menu.pharma_inventory_report.finance_inventory',
        hasBullet: true,
        permissions: PagePermission.FINANCE_INVENTORY_REPORT,
      },
      {
        to: '/pharma-inventory-report/inventory-stock',
        intlMessage: 'menu.report.inventory_stock',
        hasBullet: true,
        permissions: PagePermission.REPORT_INVENTORY_STOCK,
      },
    ],
  },
  {
    to: '',
    fontIcon: 'fa-solid fa-clipboard-list',
    intlMessage: 'menu.accounting_report',
    children: [
      {
        to: '/accounting-report/daily-transaction',
        intlMessage: 'menu.accounting_report.daily_transaction',
        hasBullet: true,
        permissions: PagePermission.REPORT_DAILY_TRANSACTION_LIST,
      },
      {
        to: '/accounting-report/sales',
        intlMessage: 'menu.accounting_report.sales',
        hasBullet: true,
        permissions: PagePermission.REPORT_SALES_LIST,
      },
      {
        to: '/accounting-report/monthly-revenue',
        intlMessage: 'menu.accounting_report.monthly_revenue',
        hasBullet: true,
        permissions: PagePermission.REPORT_MONTHLY_REVENUE_LIST,
      },
      {
        to: '/accounting-report/month-to-date-sales',
        intlMessage: 'menu.accounting_report.month_to_date_sales',
        hasBullet: true,
        permissions: PagePermission.REPORT_MONTH_TO_DATE_SALES_LIST,
      },
    ],
  },
  {
    to: '',
    fontIcon: 'fa-solid fa-clipboard-list',
    intlMessage: 'menu.management_report',
    children: [
      {
        to: '/management-report/net-promoter-score',
        intlMessage: 'menu.management_report.net-promoter-score',
        hasBullet: true,
        permissions: PagePermission.REPORT_NET_PROMOTER_SCORE_LIST,
      },
      {
        to: '/management-report/service-and-performance',
        intlMessage: 'menu.management_report.service_performance',
        hasBullet: true,
        permissions: PagePermission.REPORT_SERVIVE_PERFORMANCE_LIST,
      },
    ],
  },
]

export const AsideTabsList: Array<AsideTab> = [
  {
    type: AsideTabTypeEnum.DEFAULT,
    icon: 'fa-solid fa-house',
    intlMessage: 'aside_tabs.default',
    includedPaths: extractPaths(AsideDefaultMenu),
    children: AsideDefaultMenu,
  },
  {
    type: AsideTabTypeEnum.MEDICAL,
    icon: 'fa-solid fa-notes-medical',
    intlMessage: 'aside_tabs.medical',
    includedPaths: extractPaths(AsideMedicalMenu),
    children: AsideMedicalMenu,
  },
  {
    type: AsideTabTypeEnum.POS,
    icon: 'fa-solid fa-cash-register',
    intlMessage: 'aside_tabs.POS',
    includedPaths: extractPaths(AsidePOSMenu),
    children: AsidePOSMenu,
  },
  {
    type: AsideTabTypeEnum.PHARMACY,
    icon: 'fa-solid fa-pills',
    intlMessage: 'aside_tabs.pharmacy',
    includedPaths: extractPaths(AsidePharmacyMenu),
    children: AsidePharmacyMenu,
  },
  {
    type: AsideTabTypeEnum.HR,
    icon: 'fa-solid fa-people-group',
    intlMessage: 'aside_tabs.HR',
    includedPaths: extractPaths(AsideHRMenu),
    children: AsideHRMenu,
  },
  {
    type: AsideTabTypeEnum.MANAGEMENT,
    icon: 'fa-solid fa-bars-progress',
    intlMessage: 'aside_tabs.management',
    includedPaths: extractPaths(AsideManagementMenu),
    children: AsideManagementMenu,
  },
  {
    type: AsideTabTypeEnum.REPORT,
    icon: 'fa-solid fa-file-pen',
    intlMessage: 'aside_tabs.report',
    includedPaths: extractPaths(AsideReportMenu),
    children: AsideReportMenu,
  },
]
